import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface RelayerWorker {
  id: number;
  address: string;
  description?: string;
  network: string;
  active: boolean;
  isDefault?: boolean;
}

export type RelayerIdentifierType = 'ens' | 'custom';

export interface RelayerRegistrationState {
  relayerUrl: string;
  identifierType: RelayerIdentifierType;
  ensName: string;
  customIdentifier: string;
  workers: RelayerWorker[];
}

const initialState: RelayerRegistrationState = {
  relayerUrl: '',
  identifierType: 'custom',
  ensName: '',
  customIdentifier: '',
  workers: [],
};

const relayerRegistrationSlice = createSlice({
  name: 'relayerRegistration',
  initialState,
  reducers: {
    setRelayerUrl: (state, action: PayloadAction<string>) => {
      state.relayerUrl = action.payload;
    },
    setIdentifierType: (state, action: PayloadAction<RelayerIdentifierType>) => {
      state.identifierType = action.payload;
    },
    setEnsName: (state, action: PayloadAction<string>) => {
      state.ensName = action.payload;
    },
    setCustomIdentifier: (state, action: PayloadAction<string>) => {
      state.customIdentifier = action.payload;
    },
    addWorker: (
      state,
      action: PayloadAction<{ address: string; description?: string; network: string }>
    ) => {
      const maxId = state.workers.reduce((max, w) => Math.max(max, w.id), 0);
      const trimmedDescription = action.payload.description?.trim();
      state.workers.push({
        id: maxId + 1,
        address: action.payload.address,
        description: trimmedDescription || '—',
        network: action.payload.network,
        active: true,
        isDefault: false,
      });
    },
    removeWorker: (state, action: PayloadAction<number>) => {
      const worker = state.workers.find((w) => w.id === action.payload);
      if (!worker) return;
      if (worker.isDefault) return;
      if (state.workers.length <= 1) return;
      state.workers = state.workers.filter((w) => w.id !== action.payload);
    },
    toggleWorkerActive: (state, action: PayloadAction<number>) => {
      const worker = state.workers.find((w) => w.id === action.payload);
      if (worker) {
        worker.active = !worker.active;
      }
    },
  },
});

export const {
  setRelayerUrl,
  setIdentifierType,
  setEnsName,
  setCustomIdentifier,
  addWorker,
  removeWorker,
  toggleWorkerActive,
} = relayerRegistrationSlice.actions;

export default relayerRegistrationSlice.reducer;
